import { listaLojas, taxaDaLoja } from "../constants/lojasOnline";
import { taxaMaquininha } from "../constants/maquininhas";
import { calcPagamento } from "./pagamento";

/*
 * Venda feita fora do balcão — marketplace (Mercado Livre, Shopee, Amazon…),
 * site próprio, Instagram/WhatsApp — com tudo que sai pelo caminho até o
 * dinheiro virar lucro.
 *
 * Duas coisas diferentes, que não podem se misturar:
 *  - o que a LOJA DESCONTA antes de depositar: comissão, taxa do cartão e,
 *    no marketplace, o frete que ela cobra do vendedor. `liquido` é o que
 *    cai na conta ("você recebe" da loja) e `retido` = bruto − liquido.
 *  - o que o vendedor PAGA À PARTE: frete de site/Instagram (Correios,
 *    transportadora) e os custos adicionais (etiqueta, imposto…). Não passa
 *    pela loja, então não muda o depósito — só o lucro.
 * A embalagem (caixa de envio) vem do cadastro de Materiais e entra em
 * pedido.materiais_usados com `envio: true`: custo de material + baixa de
 * estoque pelo mesmo caminho dos outros materiais.
 *
 * Regras do pedido (pedido.venda_online):
 *  - pedido.valor é o BRUTO: o que o cliente pagou (produtos + frete cobrado
 *    + taxa do cartão, quando é o cliente que paga). É o faturamento.
 *  - venda_online.custo_venda = retido + custos à parte. calcCustoPedido soma
 *    isso ao custo de produção, então o lucro sai certo em todo lugar.
 *  - Os valores são uma FOTO do dia da venda: se a tabela da loja ou um custo
 *    salvo mudar depois, o pedido antigo não muda.
 *  - Quando o vendedor digita o "você recebe" que a loja mostra, esse número
 *    manda: a comissão varia por categoria e tipo de anúncio.
 */

export const CANAL_BALCAO = "balcao";

const ICONES = { ml: "🟡", shopee: "🟠", amazon: "📦", site: "🌐", outra: "🛒", [CANAL_BALCAO]: "🏪" };
const CANAIS_EXTRAS = [
  { id: "site", nome: "Site" },
  { id: "outra", nome: "Outra" },
];

export function iconeCanal(id) {
  return ICONES[id] || "🛍️";
}

/* Balcão + os marketplaces da tabela de taxas (a viva, via useTaxasLojas,
   então uma loja nova que o job passar a ler aparece aqui sozinha) + site
   próprio e "outra" (Instagram, WhatsApp, feira…), que não têm comissão. */
export function listaCanais(tabelaLojas) {
  const lojas = listaLojas(tabelaLojas).map(l => ({ id: l.id, nome: l.nome || l.id, marketplace: true }));
  return [{ id: CANAL_BALCAO, nome: "Balcão" }, ...lojas, ...CANAIS_EXTRAS]
    .map(c => ({ ...c, icone: iconeCanal(c.id) }));
}

// Estado do formulário: tudo string, "" = não preenchido.
export const VENDA_VAZIA = {
  canal: CANAL_BALCAO,
  numero_loja: "",
  frete_cliente: "",
  frete_vendedor: "",
  taxa_plataforma: "",   // "" = calcula pela tabela da loja
  taxa_manual: false,    // true = o vendedor digitou; false com valor = foto salva do pedido
  taxa_ref: null,        // faixa usada quando a taxa veio da tabela (foto salva)
  maquininha_id: "",
  cartao: "",            // "" sem cartão | "debito" | "1".."12" (parcelas no crédito)
  cartao_pct: "",        // "" = pela maquininha
  cartao_quem: "loja",   // quem paga a taxa do cartão: "loja" (sai do lucro) | "cliente" (vai no preço)
  custos: [],            // custos adicionais deste pedido: [{ id, nome, tipo, valor }]
  liquido_informado: "", // "" = calcula
  repasse_em: null,      // quando o repasse foi confirmado (só leitura no formulário)
  custos_no_caixa: false,
};

/* ── Custos adicionais ─────────────────────────────────────────── */

export const TIPOS_CUSTO = [
  { id: "fixo", label: "R$ por pedido" },
  { id: "por_unidade", label: "R$ por peça" },
  { id: "pct", label: "% da venda" },
];

const num = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};
const vazio = (v) => v === "" || v == null;
const r2 = (v) => Math.round(v * 100) / 100;
// Arredonda pra cima no centavo sem cair no ruído do ponto flutuante (12,300000001 → 12,30)
const acimaCentavo = (v) => Math.ceil(v * 100 - 1e-6) / 100;

export function novoCusto({ nome, tipo = "fixo", valor }) {
  return {
    id: `c${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`,
    nome: String(nome || "").trim(),
    tipo: TIPOS_CUSTO.some(t => t.id === tipo) ? tipo : "fixo",
    valor: Math.max(0, num(valor)),
  };
}

/* Quanto um custo dá neste pedido. `pct` é sobre o bruto (o que o cliente
   pagou) — é a base de imposto sobre a venda. */
export function valorDoCusto(custo, { bruto = 0, unidades = 1 } = {}) {
  const v = Math.max(0, num(custo?.valor));
  if (custo?.tipo === "pct") return r2(bruto * v / 100);
  if (custo?.tipo === "por_unidade") return r2(v * Math.max(1, unidades));
  return r2(v);
}

export function descreverCusto(c) {
  const v = num(c?.valor);
  if (c?.tipo === "pct") return `${v}% da venda`;
  const reais = v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  return c?.tipo === "por_unidade" ? `${reais} por peça` : `${reais} por pedido`;
}

/* ── Embalagem (Materiais com envio: true) ────────────────────── */

export function embalagemDoPedido(materiaisUsados) {
  const itens = (materiaisUsados || []).filter(m => m?.envio);
  const total = r2(itens.reduce((s, m) => s + num(m.custo_unitario) * num(m.qtd), 0));
  return { itens, total };
}

export function itemDeEmbalagem(material, qtd = 1) {
  return {
    id: material.id, nome: material.nome, unidade: material.unidade || "un",
    custo_unitario: num(material.custo_unitario), qtd: Math.max(1, num(qtd) || 1), envio: true,
  };
}

/* ── Conta ────────────────────────────────────────────────────── */

export function ehVendaOnline(venda) {
  return !!venda?.canal && venda.canal !== CANAL_BALCAO;
}

/* Preço UNITÁRIO de cada item: a taxa fixa das lojas é por unidade vendida e
   a faixa depende do preço da unidade, não do total do pedido (3 peças de
   R$30 pagam 3 taxas fixas da faixa de R$30, não uma da faixa de R$90).
   Quando o valor cobrado foi digitado à mão (desconto/acréscimo), distribui
   na mesma proporção dos itens. Sem itens, o pedido todo vale uma unidade. */
export function unidadesDoPedido(itens, precoProdutos) {
  const preco = Math.max(0, num(precoProdutos));
  const lista = (itens || []).map(it => ({ preco: Math.max(0, num(it.valor_unit)), qtd: Math.max(1, parseInt(it.qtd, 10) || 1) }));
  const somaItens = lista.reduce((s, u) => s + u.preco * u.qtd, 0);
  if (!lista.length || somaItens <= 0) return preco > 0 ? [{ preco, qtd: 1 }] : [];
  const fator = preco / somaItens;
  return lista.map(u => ({ preco: u.preco * fator, qtd: u.qtd }));
}

export function taxaDaLojaNoPedido(unidades, loja, hoje = new Date()) {
  let taxa = 0;
  let faixa = null;
  for (const u of unidades) {
    const r = taxaDaLoja(u.preco, loja, hoje);
    taxa += r.taxa * u.qtd;
    faixa = faixa || r.faixa;
  }
  return { taxa: r2(taxa), faixa };
}

/* A conta inteira. `lojas` é a tabela de taxas (useTaxasLojas().lojas) e
   `maquininhas` a lista de listaMaquininhas(paymentConfig).
   Retorna null no balcão. */
export function calcularVendaOnline({ venda, precoProdutos, itens, lojas, maquininhas = [], hoje = new Date() }) {
  if (!ehVendaOnline(venda)) return null;
  const loja = listaLojas(lojas).find(l => l.id === venda.canal) || null;
  const marketplace = !!loja;
  const preco = Math.max(0, num(precoProdutos));
  const freteCliente = Math.max(0, num(venda.frete_cliente));
  const freteVendedor = Math.max(0, num(venda.frete_vendedor));
  // No marketplace a loja desconta o frete do repasse; no site/Instagram o
  // vendedor paga os Correios/transportadora à parte.
  const freteRetido = marketplace ? freteVendedor : 0;
  const freteAParte = marketplace ? 0 : freteVendedor;
  const unidadesLista = unidadesDoPedido(itens, preco);
  const unidades = Math.max(1, (itens || []).reduce((s, it) => s + (Math.max(1, parseInt(it.qtd, 10) || 1)), 0));

  const auto = loja ? taxaDaLojaNoPedido(unidadesLista, loja, hoje) : { taxa: 0, faixa: null };
  const taxaFixada = !vazio(venda.taxa_plataforma);
  const taxaPlataforma = taxaFixada ? r2(Math.max(0, num(venda.taxa_plataforma))) : auto.taxa;

  const maquininha = maquininhas.find(m => m.id === venda.maquininha_id) || null;
  const pctPelaMaquininha = venda.cartao && maquininha
    ? taxaMaquininha(maquininha, venda.cartao === "debito" ? "debito" : Number(venda.cartao))
    : 0;
  const cartaoPct = vazio(venda.cartao_pct) ? pctPelaMaquininha : Math.max(0, num(venda.cartao_pct));
  // No marketplace o comprador paga pra loja: não tem como repassar a taxa pra ele.
  const cartaoQuem = !marketplace && venda.cartao_quem === "cliente" ? "cliente" : "loja";

  // Base que passa no cartão: produtos + frete cobrado. Quando o cliente paga
  // a taxa, cobra-se a mais o suficiente pra que, descontada a taxa, sobre a
  // base inteira (mesma conta do PDV — calcularRepasse).
  const base = r2(preco + freteCliente);
  const fracao = cartaoPct / 100;
  const bruto = cartaoQuem === "cliente" && fracao > 0 && fracao < 1 ? acimaCentavo(base / (1 - fracao)) : base;
  const acrescimoCartao = r2(bruto - base);
  const taxaCartao = r2(bruto * fracao);

  const liquidoCalculado = r2(bruto - taxaPlataforma - taxaCartao - freteRetido);
  const informado = !vazio(venda.liquido_informado);
  const liquido = informado ? r2(num(venda.liquido_informado)) : liquidoCalculado;
  const retido = r2(bruto - liquido);

  const custos = (venda.custos || []).map(c => ({ ...c, total: valorDoCusto(c, { bruto, unidades }) }));
  const custosTotal = r2(custos.reduce((s, c) => s + c.total, 0));
  const custosAParte = r2(freteAParte + custosTotal);

  return {
    canal: venda.canal,
    loja,
    marketplace,
    precoProdutos: r2(preco),
    freteCliente,
    freteVendedor,
    freteRetido,
    freteAParte,
    base,
    bruto,
    cartaoQuem,
    acrescimoCartao,
    taxaPlataforma,
    taxaPlataformaTabela: auto.taxa,
    taxaFixada,
    faixa: taxaFixada ? (venda.taxa_manual ? null : venda.taxa_ref) : auto.faixa,
    maquininha,
    cartaoPct: r2(cartaoPct),
    taxaCartao,
    liquidoCalculado,
    liquidoInformado: informado,
    liquido,
    ajuste: r2(liquidoCalculado - liquido),
    retido,
    unidades,
    custos,
    custosTotal,
    custosAParte,
    custoVenda: r2(retido + custosAParte),
    // O que sobra depois da venda, antes da produção e da embalagem
    sobra: r2(liquido - custosAParte),
  };
}

const refDaFaixa = (faixa) => faixa ? {
  comissao: Number(faixa.comissao) || 0,
  tipoFixo: faixa.tipoFixo || "fixo",
  valorFixo: Number(faixa.valorFixo) || 0,
  ate: Number.isFinite(Number(faixa.ate)) && faixa.ate !== null && faixa.ate !== "" ? Number(faixa.ate) : null,
} : null;

/* O que vai pro Firestore (pedido.venda_online): só números e a foto da
   faixa e dos custos usados, com de onde veio a tabela e quando foi conferida. */
export function vendaOnlineParaSalvar(venda, calc, { canalNome, origemTabela = null, conferidaEm = null } = {}) {
  if (!calc) return null;
  const daTabela = !calc.taxaFixada;
  return {
    canal: calc.canal,
    canal_nome: canalNome || calc.loja?.nome || calc.canal,
    numero_loja: String(venda.numero_loja || "").trim() || null,
    preco_produtos: calc.precoProdutos,
    frete_cliente: calc.freteCliente,
    frete_vendedor: calc.freteVendedor,
    frete_retido: calc.freteRetido,
    frete_a_parte: calc.freteAParte,
    taxa_plataforma: calc.taxaPlataforma,
    taxa_plataforma_manual: calc.taxaFixada && !!venda.taxa_manual,
    taxa_ref: daTabela
      ? (calc.faixa ? { ...refDaFaixa(calc.faixa), origem: origemTabela, conferida_em: conferidaEm } : null)
      : (venda.taxa_manual ? null : (venda.taxa_ref || null)),
    maquininha_id: calc.maquininha?.id || null,
    maquininha_nome: calc.maquininha?.nome || null,
    cartao: venda.cartao || null,
    cartao_pct: calc.cartaoPct,
    cartao_quem: calc.cartaoQuem,
    acrescimo_cartao: calc.acrescimoCartao,
    taxa_cartao: calc.taxaCartao,
    custos: calc.custos.map(c => ({ id: c.id || null, nome: c.nome || "Custo", tipo: c.tipo || "fixo", valor: num(c.valor), total: c.total })),
    custos_total: calc.custosTotal,
    custos_a_parte: calc.custosAParte,
    liquido_informado: calc.liquidoInformado ? calc.liquido : null,
    bruto: calc.bruto,
    liquido_calculado: calc.liquidoCalculado,
    liquido: calc.liquido,
    ajuste: calc.ajuste,
    retido: calc.retido,
    custo_venda: calc.custoVenda,
    repasse_em: venda.repasse_em || null,
    custos_no_caixa: !!venda.custos_no_caixa,
  };
}

/* Volta do Firestore pro formulário (editar pedido). A taxa da plataforma
   volta FIXADA no valor salvo — reabrir um pedido antigo não pode trocar a
   taxa só porque a tabela da loja mudou. "Recalcular" limpa o campo. */
export function vendaParaForm(vo, origem) {
  if (!vo?.canal) return { ...VENDA_VAZIA, canal: origem === "site" ? "site" : CANAL_BALCAO };
  const s = (v) => (v == null || v === 0 ? "" : String(v));
  const custos = (vo.custos || []).map(c => ({ id: c.id, nome: c.nome, tipo: c.tipo, valor: c.valor }));
  // Versão anterior tinha um campo único "outras taxas"
  if (num(vo.outras_taxas) > 0 && !custos.length) custos.push({ id: "outras", nome: "Outras taxas", tipo: "fixo", valor: num(vo.outras_taxas) });
  return {
    ...VENDA_VAZIA,
    canal: vo.canal,
    numero_loja: vo.numero_loja || "",
    frete_cliente: s(vo.frete_cliente),
    frete_vendedor: s(vo.frete_vendedor),
    taxa_plataforma: vo.taxa_plataforma == null ? "" : String(vo.taxa_plataforma),
    taxa_manual: !!vo.taxa_plataforma_manual,
    taxa_ref: vo.taxa_ref || null,
    maquininha_id: vo.maquininha_id || "",
    cartao: vo.cartao || "",
    cartao_pct: vo.cartao_pct == null ? "" : String(vo.cartao_pct),
    cartao_quem: vo.cartao_quem === "cliente" ? "cliente" : "loja",
    custos,
    liquido_informado: vo.liquido_informado == null ? "" : String(vo.liquido_informado),
    repasse_em: vo.repasse_em || null,
    custos_no_caixa: !!vo.custos_no_caixa,
  };
}

// Validação compartilhada entre formulário e gravação. Campos vazios são
// opcionais; zero digitado é um valor explícito e nunca significa "automático".
export function validarVendaOnline(venda, calc) {
  if (!calc) return [];
  const erros = [];
  const validar = (valor, nome, maximo = Infinity) => {
    if (vazio(valor)) return;
    const n = Number(String(valor).replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > maximo) erros.push(`${nome}: informe um valor entre zero e ${Number.isFinite(maximo) ? maximo : "um número positivo"}.`);
  };
  validar(venda.frete_cliente, "Frete do cliente");
  validar(venda.frete_vendedor, "Frete do vendedor");
  validar(venda.taxa_plataforma, "Taxa da plataforma");
  validar(venda.cartao_pct, "Taxa do cartão", 99.99);
  validar(venda.liquido_informado, "Valor do repasse", calc.bruto);
  (venda.custos || []).forEach(c => validar(c.valor, c.nome || "Custo adicional", c.tipo === "pct" ? 100 : Infinity));
  if (!Number.isFinite(calc.bruto) || !Number.isFinite(calc.liquido)) erros.push("Confira os valores da venda.");
  if (calc.liquido < 0) erros.push("As retenções ultrapassam o total da venda. Confira as taxas e o frete antes de salvar.");
  return erros;
}

const assinaturaItens = itens => JSON.stringify((itens || []).map(it => ({ id: it.produto_id || it.id || "", qtd: num(it.qtd), preco: num(it.valor_unit), custo: it.custo_unit == null ? null : num(it.custo_unit) })));
export function vendaComRepasse(pedido) {
  return !!pedido?.venda_online?.repasse_em || (pedido?.pagamentos || []).some(p => p.retido);
}

// O editor de itens deve atualizar o preço bruto e a foto financeira juntos.
// Reabrir sem alteração mantém a foto; alterar itens recalcula apenas taxas
// estimadas. O valor real digitado pelo vendedor permanece explícito.
export function recalcularVendaOnlinePedido(pedido, itens, precoProdutos, { lojas, maquininhas = [], origemTabela = null, conferidaEm = null } = {}) {
  const preco = Number(String(precoProdutos ?? 0).replace(",", "."));
  if (!Number.isFinite(preco) || preco < 0) throw new Error("Informe um preço válido para os produtos.");
  const vo = pedido.venda_online;
  const anterior = vo?.preco_produtos ?? pedido.valor ?? 0;
  const mudou = Math.abs(preco - num(anterior)) > 0.005 || assinaturaItens(itens) !== assinaturaItens(pedido.itens);
  if (mudou && vendaComRepasse(pedido)) throw new Error("Este pedido já tem repasse confirmado. Estorne o recebimento antes de alterar produtos ou valores.");
  if (!vo?.canal) return { itens, valor: r2(preco) };
  if (!mudou) return { itens, valor: num(pedido.valor), venda_online: vo };
  const venda = vendaParaForm(vo);
  if (!vo.taxa_plataforma_manual) Object.assign(venda, { taxa_plataforma: "", taxa_manual: false, taxa_ref: null });
  const calc = calcularVendaOnline({ venda, precoProdutos: preco, itens, lojas, maquininhas });
  const erros = validarVendaOnline(venda, calc);
  if (erros.length) throw new Error(erros[0]);
  return { itens, valor: calc.bruto, venda_online: { ...vo, ...vendaOnlineParaSalvar(venda, calc, { canalNome: vo.canal_nome, origemTabela, conferidaEm }) } };
}

/* ── Repasse ───────────────────────────────────────────────────── */

const retidoPrevistoDe = (vo, total) => vo.retido != null
  ? num(vo.retido)
  : vo.custo_venda != null ? num(vo.custo_venda) - num(vo.custos_a_parte)
  : vo.liquido != null ? Math.max(0, num(total) - num(vo.liquido)) : 0;

/* A loja (ou o gateway) deposita o líquido e fica com o resto. O que caiu
   entra no caixa; o que a loja reteve entra como pagamento `retido` — quita o
   pedido (faturamento = bruto) sem passar pelo caixa, porque esse dinheiro
   nunca chegou. Se caiu diferente do previsto, a venda_online é corrigida:
   o que caiu de verdade é que manda. Os custos à parte não mudam.

   `lancarCustos`: também devolve a saída de caixa dos custos à parte (frete
   dos Correios, etiqueta…) — uma vez só por pedido.

   Retorna { pagamentos, vendaOnline, entradaCaixa, saidaCaixa } ou null se
   não há saldo. */
export function montarRepasse(pedido, valorRecebido, { em = new Date().toISOString(), por = "—", id = String(Date.now()), lancarCustos = false } = {}) {
  const vo = pedido?.venda_online;
  if (!vo) return null;
  const total = num(pedido.valor);
  const lista = Array.isArray(pedido.pagamentos) ? pedido.pagamentos : [];
  const saldo = r2(calcPagamento(pedido).saldo);
  if (saldo <= 0) return null;
  if (vo.repasse_em) throw new Error("O repasse deste pedido já foi confirmado. Atualize o pedido antes de continuar.");
  const digitado = Number(String(valorRecebido ?? "").replace(",", "."));
  if (vazio(valorRecebido) || !Number.isFinite(digitado) || digitado < 0 || digitado > saldo + 0.005) throw new Error("Informe um repasse entre zero e o saldo do pedido.");
  const recebido = r2(digitado);
  const retido = r2(saldo - recebido);
  const retidoTotal = r2(lista.filter(p => p?.retido).reduce((s, p) => s + num(p.valor), 0) + retido);
  const custosAParte = num(vo.custos_a_parte);
  const nome = vo.canal_nome || "loja";

  const pagamentos = [];
  if (recebido > 0) pagamentos.push({ id: `${id}_rep`, valor: recebido, metodo: `Repasse ${nome}`, em, por });
  if (retido > 0) pagamentos.push({ id: `${id}_ret`, valor: retido, metodo: `Retido por ${nome} (taxas e frete)`, retido: true, em, por });

  const saidaCaixa = lancarCustos && !vo.custos_no_caixa ? r2(custosAParte) : 0;
  const liquido = r2(total - retidoTotal);
  const liquidoCalculado = vo.liquido_calculado ?? vo.liquido ?? liquido;
  return {
    pagamentos,
    entradaCaixa: recebido,
    saidaCaixa,
    vendaOnline: {
      ...vo,
      liquido,
      liquido_informado: liquido,
      ajuste: r2(liquidoCalculado - liquido),
      retido: retidoTotal,
      custo_venda: r2(retidoTotal + custosAParte),
      repasse_em: em,
      ...(saidaCaixa > 0 ? { custos_no_caixa: true } : {}),
    },
  };
}

/* Quanto deve cair na conta pra quitar o pedido: o saldo menos o que a loja
   ainda vai reter (o retido previsto menos o que já foi retido antes). */
export function repassePrevisto(pedido) {
  const vo = pedido?.venda_online;
  if (!vo) return 0;
  const lista = Array.isArray(pedido.pagamentos) ? pedido.pagamentos : [];
  const saldo = calcPagamento(pedido).saldo;
  const retidoAntes = lista.filter(p => p?.retido).reduce((s, p) => s + num(p.valor), 0);
  return r2(Math.min(saldo, Math.max(0, saldo - (retidoPrevistoDe(vo, pedido.valor) - retidoAntes))));
}

/* Quanto dos pagamentos saiu do caixa de verdade (sem o que a loja reteve) —
   é isso que um estorno tem que tirar do caixa. */
export function pagoNoCaixa(pedido, pagoTotal) {
  const retido = (pedido?.pagamentos || []).filter(p => p?.retido).reduce((s, p) => s + num(p.valor), 0);
  return r2(Math.max(0, pagoTotal - retido));
}

/* ── Configuração salva (users/{uid}.vendaOnline) ─────────────────
   { custos: [{ id, nome, tipo, valor }],            ← catálogo
     canais: { [canal]: { custos: [ids], embalagem: [{ id, qtd }],
                          maquininha_id, cartao, cartao_quem } } }   ← padrão de cada loja */

export function lerConfig(raw) {
  return {
    custos: Array.isArray(raw?.custos) ? raw.custos.filter(c => c?.id) : [],
    canais: raw?.canais && typeof raw.canais === "object" ? raw.canais : {},
  };
}

export function temPadrao(config, canal) {
  return !!lerConfig(config).canais[canal];
}

/* Troca de canal no formulário: zera o que é da loja anterior (taxa e
   "você recebe" dela) e, se o canal novo tem padrão salvo, aplica os custos,
   a embalagem e o cartão dele. Sem padrão, mantém o que já estava escolhido.
   Voltar pro balcão tira tudo que é da venda online. */
export function aplicarPadraoDoCanal({ venda, materiaisUsados, canal, config, materiais }) {
  const cfg = lerConfig(config);
  const padrao = canal === CANAL_BALCAO ? null : (cfg.canais[canal] || null);
  const semEnvio = (materiaisUsados || []).filter(m => !m?.envio);
  const base = { ...VENDA_VAZIA, ...venda, canal, taxa_plataforma: "", taxa_manual: false, taxa_ref: null, liquido_informado: "" };

  if (canal === CANAL_BALCAO) {
    return { venda: { ...base, custos: [], maquininha_id: "", cartao: "", cartao_pct: "", cartao_quem: "loja" }, materiaisUsados: semEnvio, padrao: null };
  }
  if (!padrao) return { venda: base, materiaisUsados: materiaisUsados || [], padrao: null };

  const custos = (padrao.custos || [])
    .map(id => cfg.custos.find(c => c.id === id))
    .filter(Boolean)
    .map(c => ({ id: c.id, nome: c.nome, tipo: c.tipo, valor: c.valor }));
  const embalagem = (padrao.embalagem || [])
    .map(e => {
      const m = (materiais || []).find(x => x.id === e.id);
      return m ? itemDeEmbalagem(m, e.qtd) : null;
    })
    .filter(Boolean);
  return {
    venda: {
      ...base, custos,
      maquininha_id: padrao.maquininha_id || "",
      cartao: padrao.maquininha_id ? (padrao.cartao || "1") : "",
      cartao_pct: "",
      cartao_quem: padrao.cartao_quem === "cliente" ? "cliente" : "loja",
    },
    materiaisUsados: [...semEnvio, ...embalagem],
    padrao,
  };
}

/* O que o botão "Salvar como padrão" grava pra este canal. Custo avulso que
   não está no catálogo (sem id conhecido) não vira padrão. */
export function padraoDoPedido(venda, materiaisUsados, config) {
  const catalogo = lerConfig(config).custos;
  return {
    custos: (venda?.custos || []).map(c => c.id).filter(id => catalogo.some(c => c.id === id)),
    embalagem: embalagemDoPedido(materiaisUsados).itens.map(m => ({ id: m.id, qtd: num(m.qtd) || 1 })),
    maquininha_id: venda?.maquininha_id || "",
    cartao: venda?.maquininha_id ? (venda.cartao || "1") : "",
    cartao_quem: venda?.cartao_quem === "cliente" ? "cliente" : "loja",
  };
}
